<orbit-dialog-header [title]="data.title" />

<div class="dialog-content">
  @let isConfirmationValue = isConfirmation();
  @if (isConfirmationValue) {
    <!-- Confirmation dialog content -->
    <div class="confirmation-message">
      <p>{{ data.message }}</p>
    </div>
  } @else {
    <!-- Template form content -->
    <form [formGroup]="templateForm" class="template-form">
      <!-- Template name (only when creating new) -->
      @if (!isEditMode()) {
        <div class="form-group">
          <label for="name">{{ 'SETTINGS.MESSAGE_TEMPLATES.NAME' | translate }} *</label>
          <input
            type="text"
            id="name"
            formControlName="name"
            [placeholder]="'SETTINGS.MESSAGE_TEMPLATES.NAME_PLACEHOLDER' | translate" />
          @if (templateForm.get('name')?.invalid && templateForm.get('name')?.touched) {
            <div class="error-message">
              @if (templateForm.get('name')?.errors?.['required']) {
                <span>
                  {{ 'SETTINGS.MESSAGE_TEMPLATES.NAME_REQUIRED' | translate }}
                </span>
              }
              @if (templateForm.get('name')?.errors?.['minlength']) {
                <span>
                  {{ 'SETTINGS.MESSAGE_TEMPLATES.NAME_MIN_LENGTH' | translate }}
                </span>
              }
              @if (templateForm.get('name')?.errors?.['maxlength']) {
                <span>
                  {{ 'SETTINGS.MESSAGE_TEMPLATES.NAME_MAX_LENGTH' | translate }}
                </span>
              }
            </div>
          }
        </div>
      }

      <!-- Template description -->
      <div class="form-group">
        <label for="description">{{ 'SETTINGS.MESSAGE_TEMPLATES.DESCRIPTION' | translate }}</label>
        <textarea
          id="description"
          formControlName="description"
          [placeholder]="'SETTINGS.MESSAGE_TEMPLATES.DESCRIPTION_PLACEHOLDER' | translate"
          rows="3"></textarea>
      </div>

      <!-- Template kind selection -->
      <div class="form-group">
        <label>{{ 'SETTINGS.MESSAGE_TEMPLATES.KIND.TITLE' | translate }} *</label>
        <div class="template-kind-options">
          @for (kind of templateKinds; track kind) {
            <orbit-form-extended-checkbox
              [type]="'radio'"
              [item]="'SETTINGS.MESSAGE_TEMPLATES.KIND.' + kind | translate"
              [name]="'template-kind'"
              [value]="kind"
              [description]="'SETTINGS.MESSAGE_TEMPLATES.KIND.DESCRIPTION.' + kind | translate"
              [controller]="templateKind" />
          }
        </div>
      </div>

      <!-- Template texts section -->
      <div class="form-section">
        <div class="section-header">
          <h3>{{ 'SETTINGS.MESSAGE_TEMPLATES.TEXTS' | translate }} *</h3>
          <button type="button" class="btn-add" (click)="addText()"
                  [disabled]="textsArray.length >= availableLanguages.length">
            <fa-icon [icon]="addIcon"></fa-icon>
            {{ 'SETTINGS.MESSAGE_TEMPLATES.ADD_LANGUAGE' | translate }}
          </button>
        </div>

        <div class="text-entries" formArrayName="texts">
          @for (textGroup of textsArray.controls; track $index) {
            <div [formGroupName]="$index" class="text-entry">
              <div class="text-entry-header">
                <div class="language-select">
                  <label [for]="'language-' + $index">{{ 'SETTINGS.MESSAGE_TEMPLATES.LANGUAGE' | translate }}</label>
                  <select [id]="'language-' + $index" formControlName="language">
                    @for (lang of availableLanguages; track lang) {
                      <option [value]="lang" [disabled]="isLanguageDisabled(lang, $index)">
                        {{ 'SETTINGS.MESSAGE_TEMPLATES.LANGUAGES.' + lang | translate }}
                      </option>
                    }
                  </select>
                </div>
                <button type="button" class="btn-remove" (click)="removeText($index)"
                        [disabled]="textsArray.length <= 1">
                  <fa-icon [icon]="deleteIcon"></fa-icon>
                </button>
              </div>

              <div class="text-field">
                <label [for]="'text-' + $index">{{ 'SETTINGS.MESSAGE_TEMPLATES.TEXT' | translate }}</label>
                <textarea [id]="'text-' + $index" formControlName="text" rows="3"
                          [placeholder]="'SETTINGS.MESSAGE_TEMPLATES.TEXT_PLACEHOLDER' | translate"></textarea>
                @if (textsArray.at($index).get('text')?.invalid && textsArray.at($index).get('text')?.touched) {
                  <div class="error-message">
                    {{ 'SETTINGS.MESSAGE_TEMPLATES.TEXT_REQUIRED' | translate }}
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>

      <!-- Template parameters section -->
      <div class="form-section">
        <div class="section-header">
          <h3>{{ 'SETTINGS.MESSAGE_TEMPLATES.PARAMETERS' | translate }}</h3>
          <button type="button" class="btn-add" (click)="addParameter()">
            <fa-icon [icon]="addIcon"></fa-icon>
            {{ 'SETTINGS.MESSAGE_TEMPLATES.ADD_PARAMETER' | translate }}
          </button>
        </div>

        <div class="parameter-entries" formArrayName="parameters">
          @for (paramGroup of parametersArray.controls; track $index) {
            <div [formGroupName]="$index" class="parameter-entry">
              <div class="parameter-entry-header">
                <div class="parameter-name">
                  <label
                    [for]="'param-name-' + $index">{{ 'SETTINGS.MESSAGE_TEMPLATES.PARAMETER_NAME' | translate }}</label>
                  <input type="text" [id]="'param-name-' + $index" formControlName="name"
                         [placeholder]="'SETTINGS.MESSAGE_TEMPLATES.PARAMETER_NAME_PLACEHOLDER' | translate" />
                  @if (parametersArray.at($index).get('name')?.invalid && parametersArray.at($index).get('name')?.touched) {
                    <div class="error-message">
                      @if (parametersArray.at($index).get('name')?.errors?.['required']) {
                        <span>
                          {{ 'SETTINGS.MESSAGE_TEMPLATES.PARAMETER_NAME_REQUIRED' | translate }}
                        </span>
                      }
                      @if (parametersArray.at($index).get('name')?.errors?.['pattern']) {
                        <span>
                          {{ 'SETTINGS.MESSAGE_TEMPLATES.PARAMETER_NAME_PATTERN' | translate }}
                        </span>
                      }
                    </div>
                  }
                </div>
                <button type="button" class="btn-remove" (click)="removeParameter($index)">
                  <fa-icon [icon]="deleteIcon"></fa-icon>
                </button>
              </div>

              <div class="parameter-type">
                <label
                  [for]="'param-type-' + $index">{{ 'SETTINGS.MESSAGE_TEMPLATES.PARAMETER_TYPE' | translate }}</label>
                <select [id]="'param-type-' + $index" formControlName="kind" class="form-control">
                  @for (type of filteredParameterTypes; track type) {
                    <option [value]="type">
                      {{ 'SETTINGS.MESSAGE_TEMPLATES.PARAMETER_TYPES.' + type | translate }}
                    </option>
                  }
                </select>
              </div>

              @if (shouldShowValuePath(parametersArray.at($index))) {
                <div class="parameter-value-path">
                  <label
                    [for]="'param-path-' + $index">{{ 'SETTINGS.MESSAGE_TEMPLATES.VALUE_PATH' | translate }}</label>
                  <div class="prefix-input-container">
                    <span class="input-prefix">{{ valuePathPrefix }}</span>
                    <input
                      type="text"
                      [id]="'param-path-' + $index"
                      formControlName="valuePath"
                      class="prefix-input form-control"
                      [placeholder]="'SETTINGS.MESSAGE_TEMPLATES.VALUE_PATH_PLACEHOLDER' | translate" />
                  </div>
                </div>
              }
            </div>
          }

          @if (parametersArray.length === 0) {
            <div class="no-parameters">
              <p>{{ 'SETTINGS.MESSAGE_TEMPLATES.NO_PARAMETERS_ADDED' | translate }}</p>
            </div>
          }
        </div>
      </div>
    </form>
  }
</div>

<orbit-dialog-footer>
  <div class="right-addon" right-addon>
    <orbit-button
      [flat]="true"
      [label]="data.cancelLabel"
      (onClick)="closeDialog(false)" />
    <orbit-button
      [disabled]="!isConfirmationValue && templateForm.invalid"
      [label]="data.confirmLabel"
      (onClick)="closeDialog(true)" />
  </div>
</orbit-dialog-footer>
